/**
 * Hourly forecast accuracy through the operating day.
 *
 * WHAT IS PLOTTED, AND WHAT IS NOT
 * --------------------------------
 * Each point is that HOUR'S OWN hit rate, obtained by differencing consecutive
 * cumulative snapshots in useAccuracyHistory. The stored snapshots are
 * day-to-date; plotting them raw would draw a running average, which converges
 * by construction and reads as "forecasts improve through the day" when it is
 * really n growing. If this chart ever looks like a smooth settling curve,
 * suspect the differencing before believing the story.
 *
 * Two series, both rates on the same 0..1 scale, so one y-axis. A second scale
 * (sample counts, MAE in persons) is deliberately NOT overlaid — it would be a
 * dual axis, which makes any two series appear to cross wherever the scales
 * happen to put them. Counts live in the tooltip and the table instead.
 *
 * Palette validated against the #0d1b2a surface: OKLCH lightness inside the
 * dark band, chroma floor, CVD separation dE 28.5 (protan) / 22.2 (tritan),
 * normal-vision 30.3, contrast >= 3:1. Do not re-tint by eye.
 */
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import type { HourlyPoint } from '../home/useAccuracyHistory';

/** Validated categorical pair — fixed order, never cycled. */
const SERIES = {
  displayed: '#c08a0a',
  model:     '#4a8bf0',
} as const;

const SURFACE = '#0d1b2a';
const INK        = 'rgba(255,255,255,0.88)';
const INK_MUTED  = 'rgba(255,255,255,0.55)';
const INK_FAINT  = 'rgba(255,255,255,0.32)';
const GRID       = 'rgba(255,255,255,0.08)';

const MONO = '"IBM Plex Mono", ui-monospace, monospace';

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;

type Props = {
  points: HourlyPoint[];
  /** Shown in the heading so the day being charted is never ambiguous. */
  day: string;
  loading?: boolean;
  error?: Error | null;
};

const LegendSwatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {/* Identity is carried by the swatch; the text stays in ink, never the
        series colour. */}
    <Box sx={{ width: 10, height: 2, borderRadius: '1px', backgroundColor: color }} />
    <Typography sx={{ fontSize: '11px', color: INK_MUTED }}>{label}</Typography>
  </Box>
);

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p: HourlyPoint = payload[0].payload;
  return (
    <Box sx={{
      backgroundColor: 'rgba(5,12,20,0.96)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '4px', px: 1.25, py: 1, fontFamily: MONO,
    }}>
      <Typography sx={{ fontSize: '11px', color: INK, mb: 0.5 }}>{label}:00–{label}:59</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 10px' }}>
        <Typography sx={{ fontSize: '11px', color: INK_MUTED }}>Meldung</Typography>
        <Typography sx={{ fontSize: '11px', color: INK, textAlign: 'right' }}>{pct(p.hitRate)}</Typography>
        <Typography sx={{ fontSize: '11px', color: INK_MUTED }}>Modell</Typography>
        <Typography sx={{ fontSize: '11px', color: INK, textAlign: 'right' }}>{pct(p.modelHitRate)}</Typography>
        <Typography sx={{ fontSize: '11px', color: INK_MUTED }}>HOCH→NIEDRIG</Typography>
        <Typography sx={{ fontSize: '11px', color: INK, textAlign: 'right' }}>{pct(p.highToLowRate)}</Typography>
      </Box>
      {/* n is what the rate rests on — an hour over 200 samples is not the same
          claim as one over 12,000, and the chart cannot show that by itself. */}
      <Typography sx={{ fontSize: '10px', color: INK_FAINT, mt: 0.75 }}>
        {p.nHour.toLocaleString('de-DE')} Stichproben in dieser Stunde
        {' · '}{p.nCumulative.toLocaleString('de-DE')} kumuliert
      </Typography>
    </Box>
  );
};

const AccuracyChart: React.FC<Props> = ({ points, day, loading, error }) => {
  const [showTable, setShowTable] = useState(false);

  // A differenced series needs two snapshots, so there is nothing to draw until
  // the second hourly run. Say so plainly rather than rendering an empty axis,
  // which reads as breakage.
  const empty = !loading && !error && points.length < 1;

  // Rates sit in a narrow band near the top, so a full 0–100% axis would flatten
  // every difference worth seeing. Padded around the data instead, with the
  // ticks always drawn so the truncation is visible rather than implied.
  const values = points.flatMap(p =>
    [p.hitRate, p.modelHitRate].filter((v): v is number => typeof v === 'number'));
  const lo = values.length ? Math.max(0, Math.min(...values) - 0.04) : 0.5;
  const hi = values.length ? Math.min(1, Math.max(...values) + 0.02) : 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '12px', color: INK, letterSpacing: '0.04em' }}>
          PROGNOSEGÜTE JE STUNDE — {day}
        </Typography>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setShowTable(v => !v)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowTable(v => !v); } }}
          sx={{
            cursor: 'pointer', fontSize: '10px', color: INK_MUTED, fontFamily: MONO,
            '&:hover': { color: INK }, '&:focus-visible': { outline: `1px solid ${INK_MUTED}` },
          }}
        >
          {showTable ? 'DIAGRAMM' : 'TABELLE'}
        </Box>
      </Box>

      {/* Identity is never colour alone: a legend is always present for two
          series, and the table view carries the same numbers without colour. */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <LegendSwatch color={SERIES.displayed} label="Meldung (93/153)" />
        <LegendSwatch color={SERIES.model} label="Modell (Pareto)" />
      </Box>

      {error && (
        <Typography sx={{ fontSize: '11px', color: '#f87171', fontFamily: MONO }}>
          {error.message.includes('permission')
            ? 'Keine Leseberechtigung für accuracy_history — Firestore-Regel nicht deployt?'
            : error.message}
        </Typography>
      )}

      {empty && (
        <Typography sx={{ fontSize: '11px', color: INK_FAINT, fontFamily: MONO, py: 3 }}>
          Noch keine Stundenwerte. Ein Stundenwert entsteht aus der Differenz
          zweier Momentaufnahmen, also erst ab dem zweiten stündlichen Lauf.
        </Typography>
      )}

      {!showTable && !empty && !error && (
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="hour" tickLine={false} axisLine={false}
                     tick={{ fontSize: 11, fill: INK_MUTED, fontFamily: MONO }} />
              <YAxis domain={[lo, hi]} tickLine={false} axisLine={false} width={44}
                     tick={{ fontSize: 11, fill: INK_MUTED, fontFamily: MONO }}
                     tickFormatter={(v: number) => `${Math.round(v * 100)}%`} />
              <Tooltip content={<ChartTooltip />}
                       cursor={{ stroke: 'rgba(255,255,255,0.22)', strokeWidth: 1 }} />
              {/* 2px strokes; markers ringed in the surface colour so an overlap
                  still reads as two points rather than one blob. */}
              <Line type="monotone" dataKey="hitRate" name="Meldung"
                    stroke={SERIES.displayed} strokeWidth={2}
                    dot={{ r: 3, fill: SERIES.displayed, stroke: SURFACE, strokeWidth: 2 }}
                    activeDot={{ r: 5, fill: SERIES.displayed, stroke: SURFACE, strokeWidth: 2 }}
                    connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="modelHitRate" name="Modell"
                    stroke={SERIES.model} strokeWidth={2}
                    dot={{ r: 3, fill: SERIES.model, stroke: SURFACE, strokeWidth: 2 }}
                    activeDot={{ r: 5, fill: SERIES.model, stroke: SURFACE, strokeWidth: 2 }}
                    connectNulls={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {showTable && !empty && (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{
            borderCollapse: 'collapse', width: '100%',
            '& th, & td': {
              fontFamily: MONO, fontSize: '11px', padding: '3px 10px',
              textAlign: 'right', whiteSpace: 'nowrap',
            },
            '& th': { color: INK_FAINT, fontWeight: 500 },
            '& td': { color: INK },
          }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ textAlign: 'left !important' }}>Std.</Box>
                <Box component="th">n (Std.)</Box>
                <Box component="th">Meldung</Box>
                <Box component="th">Modell</Box>
                <Box component="th">H→N</Box>
                <Box component="th">MAE kum.</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {points.map(p => (
                <Box component="tr" key={p.hour}>
                  <Box component="td" sx={{ textAlign: 'left !important', color: `${INK_MUTED} !important` }}>{p.hour}</Box>
                  <Box component="td">{p.nHour.toLocaleString('de-DE')}</Box>
                  <Box component="td">{pct(p.hitRate)}</Box>
                  <Box component="td">{pct(p.modelHitRate)}</Box>
                  <Box component="td">{pct(p.highToLowRate)}</Box>
                  <Box component="td">{p.maeCumulative === null ? '—' : p.maeCumulative.toFixed(1)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {!empty && !error && (
        <Typography sx={{ fontSize: '10px', color: INK_FAINT, mt: 0.75, lineHeight: 1.5 }}>
          Stundenwerte, nicht kumuliert. Eine Stichprobe zählt zu der Stunde, in
          der sie sich <em>auflöst</em> — also wenn der Zug den Zielhalt verlässt,
          nicht wenn die Prognose entstand. MAE ist kumuliert: die Dienstschnitt&shy;stelle
          liefert den Nenner nicht, der zum Differenzieren nötig wäre.
        </Typography>
      )}
    </Box>
  );
};

export default AccuracyChart;
